/**
 * Stage planner: il **motore di disegno** della piantina.
 *
 * Non è una superficie a sé: lo ospita `StageDesignerDialog`, che gli dà tutto
 * lo schermo. Prima aveva un proprio contenitore scrollabile dentro la modale
 * dell'Add Exercise, che a sua volta scrollava, con dentro un SVG `touch-none`
 * — **tre livelli di scroll annidati**, e su touch il browser doveva indovinare
 * quale intercettasse il gesto. Non si aggiusta con un `overscroll-behavior`:
 * si toglie un livello. Qui lo scroll non esiste, c'è **pan e zoom** sul
 * canvas, che è anche il modo in cui si lavora su una piantina.
 *
 * Convenzioni, dalle piantine di gara:
 *
 *  - **bersagli di fronte, campo in pianta** — una sagoma vista dall'alto è un
 *    trattino, e un trattino non si riconosce (vedi `StageShapes.js`);
 *  - **griglia metrica** con quote ogni 5 m: la prima domanda di chi allestisce
 *    è "a che distanza";
 *  - **margine fuori campo** di 2 m per lato: senza, un box di partenza posato
 *    a 1 m dal fondo finiva incollato al bordo e non c'era niente da afferrare;
 *  - **sovrapposizione libera** — un no-shoot davanti a una sagoma è una figura
 *    normalissima di stage design, quindi niente collisioni e passo di
 *    allineamento fine (10 cm), con l'ordine di disegno controllabile;
 *  - **conteggio colpi esatto** (carta × 2, metallo × 1, FITDS 9.5.1)
 *    confrontato coi colpi previsti;
 *  - **distanza calcolata dal box di partenza** e riscritta sul bersaglio a
 *    ogni spostamento, invece di lasciarla da allineare a mano.
 *
 * Lo strumento si **sceglie e poi si posa**, come in un editor da ufficio:
 * prima il click sulla palette, poi il click sul punto. Posare al centro e far
 * trascinare costringeva a due gesti su ogni elemento.
 *
 * Il trascinamento usa i **Pointer Events** e la matrice dell'SVG
 * (`getScreenCTM`): coprono mouse e touch con lo stesso codice e tengono conto
 * di zoom, pan e scala CSS senza rifare i conti a mano.
 *
 * Spec: docs/shooting/13-piano-revisione-test-federico.md §Gruppo B
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	ChevronsDown,
	ChevronsUp,
	Hand,
	Minus,
	MousePointer2,
	Plus,
	RotateCw,
	Trash2,
	Undo2,
	ZoomIn,
	ZoomOut,
} from "lucide-react";
import { Button } from "../ui/button";
import { StageShape } from "./StageShapes";
import {
	courseClass,
	defaultHitsRequired,
	distanceFromStart,
	elementSpec,
	paletteFor,
	requiredShots,
	validateStage,
} from "./stageElements";
import { plannedSeriesCount } from "./shootingPlan";

const DEFAULT_WIDTH_M = 20;
const DEFAULT_DEPTH_M = 25;
// 10 cm: abbastanza fine da sovrapporre un no-shoot a una sagoma, abbastanza
// grossolano da non dover essere precisi col dito.
const SNAP_M = 0.1;
const PX_PER_M = 26;
// Fuori campo, per lato. È ciò che rende afferrabile un elemento posato sul
// bordo — il box di partenza nasce a 1.5 m dal fondo e prima finiva a 13 px
// dal bordo inferiore dell'SVG, senza niente attorno su cui trascinarlo.
const MARGIN_M = 2;
// I bersagli in scala reale su un campo di 20 m sono minuscoli: le piantine di
// gara li disegnano ingranditi rispetto al campo per leggerli a colpo d'occhio.
// Gli elementi di campo (muri, box) restano invece in scala vera.
const TARGET_MAGNIFY = 2.4;
const MAX_ZOOM = 8;
const UNDO_DEPTH = 40;

const ANNOTATION_TOOLS = [
	{ value: "annot_circle", label: "Cerchio" },
	{ value: "annot_rect", label: "Rettangolo" },
	{ value: "annot_arrow", label: "Freccia" },
	{ value: "annot_text", label: "Testo" },
];
const ANNOTATION_COLOR = "#e11d48";

const snap = (value) => Math.round(value / SNAP_M) * SNAP_M;
const round1 = (value) => Math.round(value * 10) / 10;

export function StagePlanner({
	prescription = {},
	onChange,
	plannedRounds,
	discipline = "ipsc",
	readOnly = false,
	onRequestSvg,
}) {
	const svgRef = useRef(null);
	const dragRef = useRef(null);
	const [selected, setSelected] = useState(null);
	const [tool, setTool] = useState(null);
	const [zoom, setZoom] = useState(1);
	const [pan, setPan] = useState({ x: 0, y: 0 });
	const [panning, setPanning] = useState(false);
	const undoRef = useRef([]);
	const [canUndo, setCanUndo] = useState(false);

	const widthM = prescription.stage_width_m || DEFAULT_WIDTH_M;
	const depthM = prescription.stage_depth_m || DEFAULT_DEPTH_M;
	const targets = useMemo(
		() => prescription.targets || [],
		[prescription.targets],
	);
	const layout = useMemo(
		() => prescription.stage_layout || [],
		[prescription.stage_layout],
	);
	const annotations = useMemo(
		() => prescription.stage_annotations || [],
		[prescription.stage_annotations],
	);
	const startBox = layout.find((el) => el.element_type === "start_box");
	const needed = requiredShots(targets);
	// La disposizione si spara una volta per serie: il conteggio qui sotto è
	// **per serie**, e lo si dice, con il totale accanto quando le serie sono
	// più d'una. «Colpi totale non è chiaro» nasceva da questo numero senza
	// quella parola.
	const seriesCount = plannedSeriesCount(prescription);
	const warnings = useMemo(() => validateStage(prescription), [prescription]);
	const klass = courseClass(needed);
	const palette = useMemo(() => paletteFor(discipline), [discipline]);

	// ── Mondo: il campo più il margine fuori campo, tutto in unità SVG ──
	const fullW = (widthM + MARGIN_M * 2) * PX_PER_M;
	const fullH = (depthM + MARGIN_M * 2) * PX_PER_M;

	// Y cresce verso l'alto nella realtà e verso il basso in SVG: la
	// conversione sta qui e in un punto solo.
	const toSvg = useCallback(
		(xM, yM) => ({
			x: (xM + widthM / 2 + MARGIN_M) * PX_PER_M,
			y: (depthM + MARGIN_M - yM) * PX_PER_M,
		}),
		[widthM, depthM],
	);

	/**
	 * Schermo -> metri, passando per la matrice dell'SVG.
	 *
	 * `getScreenCTM` tiene conto di viewBox, pan, zoom e scala CSS in un colpo
	 * solo: rifarne i conti a mano era la ragione per cui il trascinamento
	 * saltava appena si cambiava zoom.
	 */
	const toMeters = useCallback(
		(clientX, clientY) => {
			const svg = svgRef.current;
			if (!svg?.getScreenCTM) return { x_m: 0, y_m: 0 };
			const matrix = svg.getScreenCTM();
			if (!matrix) return { x_m: 0, y_m: 0 };
			const point = new DOMPoint(clientX, clientY).matrixTransform(
				matrix.inverse(),
			);
			return {
				x_m: snap(point.x / PX_PER_M - widthM / 2 - MARGIN_M),
				y_m: snap(depthM + MARGIN_M - point.y / PX_PER_M),
			};
		},
		[widthM, depthM],
	);

	// ── Modifica, con storia ──
	const pushUndo = useCallback(() => {
		undoRef.current = [
			...undoRef.current.slice(-(UNDO_DEPTH - 1)),
			{
				targets: prescription.targets,
				stage_layout: prescription.stage_layout,
				stage_annotations: prescription.stage_annotations,
			},
		];
		setCanUndo(true);
	}, [
		prescription.targets,
		prescription.stage_layout,
		prescription.stage_annotations,
	]);

	const update = (changes) => onChange?.({ ...prescription, ...changes });

	/** Una modifica che si può annullare: la storia si tocca solo qui. */
	const commit = (changes) => {
		if (readOnly) return;
		pushUndo();
		update(changes);
	};

	const undo = () => {
		const previous = undoRef.current.pop();
		setCanUndo(undoRef.current.length > 0);
		if (!previous) return;
		setSelected(null);
		update(previous);
	};

	const LISTS = {
		targets: "targets",
		layout: "stage_layout",
		annotations: "stage_annotations",
	};
	const listOf = (list) =>
		list === "targets" ? targets : list === "layout" ? layout : annotations;

	const updateItem = (list, index, changes, { silent = false } = {}) => {
		const next = listOf(list).map((item, i) =>
			i === index ? { ...item, ...changes } : item,
		);
		// Il trascinamento produce decine di aggiornamenti al secondo: uno
		// snapshot per ciascuno riempirebbe la storia di micro-passi e
		// l'annulla non tornerebbe mai dove serve. Si registra all'inizio del
		// gesto, non a ogni frame.
		if (silent) update({ [LISTS[list]]: next });
		else commit({ [LISTS[list]]: next });
	};

	// ── Posare un elemento ──
	const placeAt = (value, xM, yM) => {
		const position = { x_m: round1(xM), y_m: round1(yM), rotation_deg: 0 };

		if (value.startsWith("annot_")) {
			const kind = value.slice("annot_".length);
			commit({
				stage_annotations: [
					...annotations,
					{
						kind,
						...position,
						w_m: kind === "text" ? null : 1.2,
						h_m: kind === "text" ? null : 1.2,
						// La freccia nasce già lunga, o non si capisce cos'è.
						x2_m: kind === "arrow" ? round1(xM + 2) : null,
						y2_m: kind === "arrow" ? round1(yM) : null,
						text: kind === "text" ? "Nota" : null,
						color: ANNOTATION_COLOR,
					},
				],
			});
			setSelected({ list: "annotations", index: annotations.length });
			return;
		}

		const spec = elementSpec(value);
		if (spec.group === "target") {
			const target = {
				count: 1,
				target_type: value,
				distance_m: null,
				...position,
			};
			if (startBox) target.distance_m = distanceFromStart(target, startBox);
			commit({ targets: [...targets, target] });
			setSelected({ list: "targets", index: targets.length });
			return;
		}

		const isStart = value === "start_box";
		// Un solo box di partenza: se ce n'è già uno si sostituisce.
		const base = isStart
			? layout.filter((el) => el.element_type !== "start_box")
			: layout;
		commit({
			stage_layout: [
				...base,
				{ element_type: value, ...position, length_m: spec.widthM },
			],
		});
		setSelected({ list: "layout", index: base.length });
	};

	const moveInOrder = (direction) => {
		if (!selected) return;
		const source = [...listOf(selected.list)];
		const target = selected.index + direction;
		if (target < 0 || target >= source.length) return;
		[source[selected.index], source[target]] = [
			source[target],
			source[selected.index],
		];
		commit({ [LISTS[selected.list]]: source });
		setSelected({ ...selected, index: target });
	};

	const selectedItem = selected ? listOf(selected.list)[selected.index] : null;
	const selectedSpec =
		selectedItem && selected.list !== "annotations"
			? elementSpec(selectedItem.target_type || selectedItem.element_type)
			: null;

	const resizeSelected = (delta) => {
		if (!selectedItem) return;
		if (selected.list === "annotations") {
			if (selectedItem.kind === "text" || selectedItem.kind === "arrow") return;
			updateItem(selected.list, selected.index, {
				w_m: Math.max(0.3, round1((selectedItem.w_m || 1.2) + delta)),
				h_m: Math.max(0.3, round1((selectedItem.h_m || 1.2) + delta)),
			});
			return;
		}
		if (!selectedSpec?.resizable) return;
		updateItem(selected.list, selected.index, {
			length_m: Math.max(
				0.3,
				round1((selectedItem.length_m || selectedSpec.widthM) + delta),
			),
		});
	};

	const removeSelected = () => {
		if (!selected) return;
		commit({
			[LISTS[selected.list]]: listOf(selected.list).filter(
				(_, i) => i !== selected.index,
			),
		});
		setSelected(null);
	};

	// ── Trascinamento ──
	const handlePointerDown = (event, list, index) => {
		if (readOnly) return;
		event.stopPropagation();
		// Con uno strumento attivo, toccare un elemento **lo seleziona** e
		// posa lo strumento: prima l'evento risaliva al canvas, che posava
		// un'altra sagoma sopra quella che si voleva cancellare — «continua
		// ad aggiungermela». Toccare una cosa che c'è già non è mai «mettine
		// un'altra qui».
		if (tool) setTool(null);
		event.currentTarget.setPointerCapture?.(event.pointerId);
		// Uno snapshot per gesto, non per frame.
		pushUndo();
		dragRef.current = { list, index };
		setSelected({ list, index });
	};

	const handlePointerMove = (event) => {
		if (!dragRef.current) return;
		const { list, index } = dragRef.current;
		const { x_m, y_m } = toMeters(event.clientX, event.clientY);
		// Si può uscire dal campo fin dentro il margine: è esattamente ciò che
		// serve per riprendere un elemento finito sul bordo.
		const limitX = widthM / 2 + MARGIN_M;
		const changes = {
			x_m: round1(Math.max(-limitX, Math.min(limitX, x_m))),
			y_m: round1(Math.max(-MARGIN_M, Math.min(depthM + MARGIN_M, y_m))),
		};
		if (list === "targets" && startBox) {
			changes.distance_m = distanceFromStart(
				{ x_m: changes.x_m, y_m: changes.y_m },
				startBox,
			);
		}
		updateItem(list, index, changes, { silent: true });
	};

	const endDrag = () => {
		dragRef.current = null;
	};

	const handleCanvasPointerDown = (event) => {
		if (tool && !readOnly) {
			const { x_m, y_m } = toMeters(event.clientX, event.clientY);
			placeAt(tool, x_m, y_m);
			return;
		}
		setSelected(null);
	};

	// ── Scorciatoie: Canc elimina, R ruota, Esc deseleziona, Ctrl+Z annulla ──
	useEffect(() => {
		if (readOnly) return undefined;
		const onKey = (event) => {
			const tag = event.target?.tagName;
			if (tag === "INPUT" || tag === "TEXTAREA") return;
			if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "z") {
				event.preventDefault();
				undo();
				return;
			}
			if (event.key === "Escape") {
				setTool(null);
				setSelected(null);
				return;
			}
			if (event.key === "Delete" || event.key === "Backspace") {
				if (!selected) return;
				event.preventDefault();
				removeSelected();
				return;
			}
			if (event.key.toLowerCase() === "r" && selectedItem) {
				updateItem(selected.list, selected.index, {
					rotation_deg: ((selectedItem.rotation_deg || 0) + 15) % 360,
				});
			}
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	});

	// Il dialog chiede l'SVG per esportarlo in PNG: glielo si passa invece di
	// fargli cercare un nodo nel DOM.
	useEffect(() => {
		onRequestSvg?.(svgRef);
	}, [onRequestSvg]);

	// ── Viewport: zoom e pan sul viewBox, non sullo scroll di un div ──
	const viewW = fullW / zoom;
	const viewH = fullH / zoom;
	const maxPanX = Math.max(0, fullW - viewW);
	const maxPanY = Math.max(0, fullH - viewH);
	const viewX = Math.min(Math.max(pan.x, 0), maxPanX);
	const viewY = Math.min(Math.max(pan.y, 0), maxPanY);

	const setZoomAround = (next) => {
		const clamped = Math.min(MAX_ZOOM, Math.max(1, round1(next)));
		// Si ingrandisce verso il centro di ciò che si sta guardando, non
		// verso l'angolo in alto a sinistra.
		const cx = viewX + viewW / 2;
		const cy = viewY + viewH / 2;
		const nextW = fullW / clamped;
		const nextH = fullH / clamped;
		setZoom(clamped);
		setPan({ x: cx - nextW / 2, y: cy - nextH / 2 });
	};

	const panDragRef = useRef(null);
	const handleFramePointerDown = (event) => {
		if (!panning) return;
		panDragRef.current = {
			x: event.clientX,
			y: event.clientY,
			px: viewX,
			py: viewY,
		};
		event.currentTarget.setPointerCapture?.(event.pointerId);
	};
	const handleFramePointerMove = (event) => {
		const start = panDragRef.current;
		if (!start) return;
		const rect = svgRef.current?.getBoundingClientRect();
		if (!rect) return;
		const scale = viewW / rect.width;
		setPan({
			x: start.px - (event.clientX - start.x) * scale,
			y: start.py - (event.clientY - start.y) * scale,
		});
	};

	// ── Disegno ──
	const renderItem = (item, list, index) => {
		const type = item.target_type || item.element_type;
		const spec = elementSpec(type);
		const { x, y } = toSvg(item.x_m ?? 0, item.y_m ?? 0);
		// I bersagli si ingrandiscono per leggibilità; muri e box restano in scala.
		const magnify = spec.group === "target" ? TARGET_MAGNIFY : 1;
		const widthPx = (item.length_m || spec.widthM) * PX_PER_M * magnify;
		const heightPx = spec.heightM * PX_PER_M * magnify;
		const isSelected = selected?.list === list && selected?.index === index;
		// I bersagli si disegnano appoggiati al punto: il piede della sagoma sta
		// sulla posizione, come su una piantina vera. Gli elementi di campo sono
		// centrati sul punto perché sono visti in pianta.
		const isFrontView = spec.group === "target";
		const offsetY = isFrontView ? -heightPx : -heightPx / 2;

		return (
			<g
				key={`${list}_${index}_${type}`}
				transform={`translate(${x}, ${y}) rotate(${item.rotation_deg || 0})`}
				onPointerDown={(e) => handlePointerDown(e, list, index)}
				style={{
					cursor: readOnly ? "default" : "grab",
					touchAction: "none",
				}}
			>
				<g transform={`translate(${-widthPx / 2}, ${offsetY})`}>
					<StageShape
						type={type}
						widthPx={widthPx}
						heightPx={heightPx}
						selected={isSelected}
					/>
				</g>
			</g>
		);
	};

	const renderAnnotation = (item, index) => {
		const { x, y } = toSvg(item.x_m ?? 0, item.y_m ?? 0);
		const isSelected =
			selected?.list === "annotations" && selected?.index === index;
		const color = item.color || ANNOTATION_COLOR;
		const w = (item.w_m || 1.2) * PX_PER_M;
		const h = (item.h_m || 1.2) * PX_PER_M;
		const sw = isSelected ? 4 : 2.5;

		let shape = null;
		if (item.kind === "circle") {
			shape = (
				<ellipse rx={w / 2} ry={h / 2} fill="none" stroke={color} strokeWidth={sw} />
			);
		} else if (item.kind === "rect") {
			shape = (
				<rect
					x={-w / 2}
					y={-h / 2}
					width={w}
					height={h}
					fill="none"
					stroke={color}
					strokeWidth={sw}
				/>
			);
		} else if (item.kind === "arrow") {
			const end = toSvg(item.x2_m ?? item.x_m, item.y2_m ?? item.y_m);
			shape = (
				<g>
					<line
						x1={0}
						y1={0}
						x2={end.x - x}
						y2={end.y - y}
						stroke={color}
						strokeWidth={sw}
						markerEnd="url(#stage-arrow)"
					/>
				</g>
			);
		} else {
			shape = (
				<text
					fill={color}
					fontSize={15}
					fontWeight="600"
					textAnchor="middle"
					dominantBaseline="middle"
				>
					{item.text || "Nota"}
				</text>
			);
		}

		return (
			<g
				key={`annot_${index}`}
				transform={`translate(${x}, ${y}) rotate(${item.rotation_deg || 0})`}
				onPointerDown={(e) => handlePointerDown(e, "annotations", index)}
				style={{ cursor: readOnly ? "default" : "grab", touchAction: "none" }}
			>
				{shape}
				{isSelected && (
					<circle r={5} fill="none" stroke="#00d68f" strokeWidth={2} />
				)}
			</g>
		);
	};

	const toolButton = (element) => {
		const active = tool === element.value;
		return (
			<button
				key={element.value}
				type="button"
				onClick={() => setTool(active ? null : element.value)}
				title={element.label}
				className={`flex items-center gap-1.5 rounded-sm border px-2 h-8 text-[11px] whitespace-nowrap transition-colors ${
					active
						? "border-volt bg-volt/15 text-volt font-medium"
						: "border-border text-muted-foreground hover:text-foreground hover:border-volt/40"
				}`}
			>
				{element.color && (
					<span
						className="w-2.5 h-2.5 rounded-sm shrink-0 border border-black/20"
						style={{ background: element.color }}
					/>
				)}
				{element.label}
			</button>
		);
	};

	return (
		<div className="flex flex-col h-full min-h-0">
			{/* ── Barra strumenti: si sceglie, poi si posa ── */}
			{!readOnly && (
				<div className="shrink-0 space-y-1.5 pb-2">
					<ToolGroup title="Carta">{palette.paper.map(toolButton)}</ToolGroup>
					<ToolGroup title="Metallo">{palette.steel.map(toolButton)}</ToolGroup>
					<ToolGroup title="Campo">{palette.layout.map(toolButton)}</ToolGroup>
					<ToolGroup title="Note">
						{ANNOTATION_TOOLS.map(toolButton)}
					</ToolGroup>
				</div>
			)}

			{/* ── Controlli di vista ── */}
			<div className="shrink-0 flex items-center gap-1 pb-1.5">
				{!readOnly && (
					<>
						<Button
							type="button"
							size="sm"
							variant={tool ? "outline" : "secondary"}
							className="rounded-sm h-7 text-[11px] px-2"
							onClick={() => setTool(null)}
							title="Seleziona (Esc)"
						>
							<MousePointer2 className="w-3.5 h-3.5 mr-1" />
							Seleziona
						</Button>
						<Button
							type="button"
							size="sm"
							variant={panning ? "secondary" : "outline"}
							className="rounded-sm h-7 text-[11px] px-2"
							onClick={() => setPanning((p) => !p)}
							title="Sposta la vista"
						>
							<Hand className="w-3.5 h-3.5" />
						</Button>
						<Button
							type="button"
							size="icon"
							variant="ghost"
							className="h-7 w-7 rounded-sm"
							onClick={undo}
							disabled={!canUndo}
							title="Annulla (Ctrl+Z)"
						>
							<Undo2 className="w-3.5 h-3.5" />
						</Button>
					</>
				)}
				<div className="flex-1" />
				<Button
					type="button"
					size="icon"
					variant="ghost"
					className="h-7 w-7 rounded-sm"
					onClick={() => setZoomAround(zoom - (zoom > 2 ? 1 : 0.25))}
					disabled={zoom <= 1}
					title="Riduci"
				>
					<ZoomOut className="w-3.5 h-3.5" />
				</Button>
				<span className="text-[11px] tabular-nums text-muted-foreground w-10 text-center">
					{Math.round(zoom * 100)}%
				</span>
				<Button
					type="button"
					size="icon"
					variant="ghost"
					className="h-7 w-7 rounded-sm"
					onClick={() => setZoomAround(zoom + (zoom >= 2 ? 1 : 0.25))}
					disabled={zoom >= MAX_ZOOM}
					title="Ingrandisci"
				>
					<ZoomIn className="w-3.5 h-3.5" />
				</Button>
			</div>

			{/* ── Canvas: unico, senza scroll, occupa lo spazio che resta ── */}
			<div className="flex-1 min-h-0 rounded-sm border border-border bg-white overflow-hidden">
				<svg
					ref={svgRef}
					viewBox={`${viewX} ${viewY} ${viewW} ${viewH}`}
					className="block w-full h-full touch-none select-none"
					style={{ cursor: panning ? "grab" : tool ? "crosshair" : "default" }}
					onPointerMove={(e) => {
						handlePointerMove(e);
						handleFramePointerMove(e);
					}}
					onPointerUp={() => {
						endDrag();
						panDragRef.current = null;
					}}
					onPointerLeave={() => {
						endDrag();
						panDragRef.current = null;
					}}
					onPointerDown={(e) => {
						handleFramePointerDown(e);
						if (!panning) handleCanvasPointerDown(e);
					}}
				>
					<defs>
						<marker
							id="stage-arrow"
							markerWidth="8"
							markerHeight="8"
							refX="7"
							refY="4"
							orient="auto"
						>
							<path d="M0 0 L8 4 L0 8 Z" fill={ANNOTATION_COLOR} />
						</marker>
					</defs>

					{/* Fuori campo in grigio: dice dove finisce il campo e dà
					    qualcosa su cui trascinare un elemento sul bordo. */}
					<rect x={0} y={0} width={fullW} height={fullH} fill="#eef1f4" />
					<rect
						x={MARGIN_M * PX_PER_M}
						y={MARGIN_M * PX_PER_M}
						width={widthM * PX_PER_M}
						height={depthM * PX_PER_M}
						fill="#ffffff"
						stroke="#cbd5e1"
					/>

					{Array.from({ length: Math.floor(depthM) + 1 }, (_, i) => (
						<line
							key={`h${i}`}
							x1={MARGIN_M * PX_PER_M}
							y1={(depthM + MARGIN_M - i) * PX_PER_M}
							x2={(widthM + MARGIN_M) * PX_PER_M}
							y2={(depthM + MARGIN_M - i) * PX_PER_M}
							stroke={i % 5 === 0 ? "#cbd5e1" : "#eef2f6"}
						/>
					))}
					{Array.from({ length: Math.floor(widthM) + 1 }, (_, i) => (
						<line
							key={`v${i}`}
							x1={(i + MARGIN_M) * PX_PER_M}
							y1={MARGIN_M * PX_PER_M}
							x2={(i + MARGIN_M) * PX_PER_M}
							y2={(depthM + MARGIN_M) * PX_PER_M}
							stroke={i % 5 === 0 ? "#cbd5e1" : "#eef2f6"}
						/>
					))}
					{Array.from({ length: Math.floor(depthM / 5) + 1 }, (_, i) => i * 5)
						.filter((m) => m > 0)
						.map((m) => (
							<text
								key={`lbl${m}`}
								x={MARGIN_M * PX_PER_M + 6}
								y={(depthM + MARGIN_M - m) * PX_PER_M - 4}
								fill="#64748b"
								fontSize="11"
							>
								{m} m
							</text>
						))}

					{/* Il campo prima, i bersagli sopra, le note in cima: un muro
					    non deve coprire una sagoma che gli sta davanti, e una
					    nota non deve finire sotto niente. Dentro ciascuna lista
					    l'ordine è controllabile, ed è ciò che permette di
					    comporre un no-shoot davanti a un bersaglio. */}
					{layout.map((item, index) => renderItem(item, "layout", index))}
					{targets.map((item, index) => renderItem(item, "targets", index))}
					{annotations.map(renderAnnotation)}
				</svg>
			</div>

			{/* ── Elemento selezionato ── */}
			{selectedItem && !readOnly && (
				<div className="shrink-0 mt-1.5 flex items-center gap-1 rounded-sm border border-volt/30 bg-volt/[0.06] px-2 py-1.5 flex-wrap">
					<div className="flex-1 min-w-0 text-[11px]">
						<span className="font-medium">
							{selectedSpec?.label || `Nota · ${selectedItem.kind}`}
						</span>
						<span className="text-muted-foreground">
							{" · "}
							{selectedItem.x_m?.toFixed(1)} / {selectedItem.y_m?.toFixed(1)} m
							{selectedItem.distance_m
								? ` · ${selectedItem.distance_m} m dal box`
								: ""}
						</span>
					</div>
					{/* Colpi che contano su questo bersaglio. È qui, sul singolo
					    bersaglio, che si scrive «5 colpi» per il lento mirato:
					    senza, il tiratore trovava la scheda ferma ai 2 del
					    regolamento. */}
					{selected.list === "targets" &&
						defaultHitsRequired(selectedItem.target_type) > 0 && (
							<div className="flex items-center gap-0.5 rounded-sm border border-border bg-background px-1">
								<span className="text-[10px] uppercase tracking-wider text-muted-foreground px-1">
									Colpi
								</span>
								<Button
									type="button"
									size="icon"
									variant="ghost"
									className="h-6 w-6 rounded-sm"
									onClick={() =>
										updateItem(selected.list, selected.index, {
											hits_required: Math.max(
												1,
												(selectedItem.hits_required ??
													defaultHitsRequired(selectedItem.target_type)) - 1,
											),
										})
									}
									title="Un colpo in meno"
								>
									<Minus className="w-3 h-3" />
								</Button>
								<span className="text-xs font-semibold tabular-nums w-5 text-center">
									{selectedItem.hits_required ??
										defaultHitsRequired(selectedItem.target_type)}
								</span>
								<Button
									type="button"
									size="icon"
									variant="ghost"
									className="h-6 w-6 rounded-sm"
									onClick={() =>
										updateItem(selected.list, selected.index, {
											hits_required:
												(selectedItem.hits_required ??
													defaultHitsRequired(selectedItem.target_type)) + 1,
										})
									}
									title="Un colpo in più"
								>
									<Plus className="w-3 h-3" />
								</Button>
							</div>
						)}
					{selected.list === "annotations" &&
						selectedItem.kind === "text" && (
							<input
								value={selectedItem.text || ""}
								onChange={(e) =>
									updateItem(selected.list, selected.index, {
										text: e.target.value,
									})
								}
								className="h-7 rounded-sm border border-border bg-background px-2 text-[11px] w-32"
								placeholder="Testo"
							/>
						)}
					{(selectedSpec?.resizable ||
						(selected.list === "annotations" &&
							["circle", "rect"].includes(selectedItem.kind))) && (
						<>
							<Button
								type="button"
								size="icon"
								variant="ghost"
								className="h-7 w-7 rounded-sm"
								onClick={() => resizeSelected(-0.5)}
								title="Rimpicciolisci"
							>
								<Minus className="w-3.5 h-3.5" />
							</Button>
							<Button
								type="button"
								size="icon"
								variant="ghost"
								className="h-7 w-7 rounded-sm"
								onClick={() => resizeSelected(0.5)}
								title="Ingrandisci"
							>
								<Plus className="w-3.5 h-3.5" />
							</Button>
						</>
					)}
					<Button
						type="button"
						size="icon"
						variant="ghost"
						className="h-7 w-7 rounded-sm"
						onClick={() =>
							updateItem(selected.list, selected.index, {
								rotation_deg: ((selectedItem.rotation_deg || 0) + 15) % 360,
							})
						}
						title="Ruota di 15° (R)"
					>
						<RotateCw className="w-3.5 h-3.5" />
					</Button>
					<Button
						type="button"
						size="icon"
						variant="ghost"
						className="h-7 w-7 rounded-sm"
						onClick={() => moveInOrder(1)}
						title="Porta avanti"
					>
						<ChevronsUp className="w-3.5 h-3.5" />
					</Button>
					<Button
						type="button"
						size="icon"
						variant="ghost"
						className="h-7 w-7 rounded-sm"
						onClick={() => moveInOrder(-1)}
						title="Porta dietro"
					>
						<ChevronsDown className="w-3.5 h-3.5" />
					</Button>
					<Button
						type="button"
						size="icon"
						variant="ghost"
						className="h-7 w-7 rounded-sm hover:text-red-500"
						onClick={removeSelected}
						title="Elimina (Canc)"
					>
						<Trash2 className="w-3.5 h-3.5" />
					</Button>
				</div>
			)}

			{/* ── Conteggio e avvisi di regolamento ── */}
			<div className="shrink-0 mt-1.5 space-y-1">
				<div className="flex items-center justify-between text-[11px] px-1 gap-2 flex-wrap">
					<span className="text-muted-foreground">
						{targets.length} bersagli ·{" "}
						<span className="text-foreground font-medium">
							{needed} colpi per serie
						</span>
						{seriesCount > 1 && needed > 0
							? ` · ×${seriesCount} serie = ${needed * seriesCount} colpi`
							: ""}
						{klass ? ` · ${klass.label}` : ""}
					</span>
					{plannedRounds ? (
						<span
							className={
								plannedRounds === needed
									? "text-muted-foreground"
									: "text-amber-400"
							}
						>
							dichiarati {plannedRounds} per serie
						</span>
					) : null}
				</div>
				{/* Avvisi, mai divieti: in allenamento si allestisce anche fuori
				    regolamento di proposito, e l'avviso è un'informazione. */}
				{warnings.map((warning) => (
					<p
						key={warning.rule + warning.text}
						className="text-[11px] text-amber-400 px-1"
					>
						<span className="opacity-60">{warning.rule}</span> · {warning.text}
					</p>
				))}
			</div>
		</div>
	);
}

function ToolGroup({ title, children }) {
	return (
		<div className="flex items-center gap-1.5">
			<span className="text-[10px] uppercase tracking-wider text-muted-foreground w-14 shrink-0">
				{title}
			</span>
			<div className="flex flex-wrap gap-1 min-w-0">{children}</div>
		</div>
	);
}
